import React, { useState, useEffect, useRef } from 'react';

const SUPABASE_FUNCTION_URL = process.env.REACT_APP_SUPABASE_URL
  ? `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-application`
  : null;

const ALLOWED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

const INITIAL = { name: '', email: '', phone: '' };

export default function ApplyModal({ job, onClose, phonePlaceholder = '+1 (555) 000-0000', sourceRegion = 'USA' }) {
  const [form, setForm] = useState(INITIAL);
  const [resumeFile, setResumeFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const overlayRef = useRef(null);
  const firstInputRef = useRef(null);

  // Focus first input when modal opens
  useEffect(() => {
    firstInputRef.current?.focus();
  }, []);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Prevent background scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  function validate() {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Full name is required.';
    if (!form.email.trim()) {
      errs.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) {
      errs.email = 'Enter a valid email address.';
    }
    if (!form.phone.trim()) {
      errs.phone = 'Phone number is required.';
    } else if (!/^\+?[\d\s\-().]{7,20}$/.test(form.phone.trim())) {
      errs.phone = 'Enter a valid phone number.';
    }
    if (!resumeFile) {
      errs.resume = 'Please attach your resume.';
    } else if (!ALLOWED_TYPES.includes(resumeFile.type)) {
      errs.resume = 'Only PDF or Word documents are accepted.';
    } else if (resumeFile.size > MAX_FILE_SIZE) {
      errs.resume = 'File must be under 5 MB.';
    }
    return errs;
  }

  function handleChange(e) {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    setErrors(errs => ({ ...errs, [e.target.name]: undefined }));
  }

  function handleFileChange(e) {
    const file = e.target.files[0] || null;
    setResumeFile(file);
    setErrors(errs => ({ ...errs, resume: undefined }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setStatus('submitting');
    setErrorMsg('');

    try {
      if (!SUPABASE_FUNCTION_URL) {
        throw new Error('Careers service is not configured. Please contact support.');
      }

      const fd = new FormData();
      fd.append('name', form.name.trim());
      fd.append('email', form.email.trim());
      fd.append('phone', form.phone.trim());
      fd.append('jobId', job.id);
      fd.append('jobTitle', job.title);
      fd.append('jobLocation', job.location);
      fd.append('sourceRegion', sourceRegion);
      fd.append('file', resumeFile);

      const res = await fetch(SUPABASE_FUNCTION_URL, {
        method: 'POST',
        body: fd,
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(json.error || `Server error (${res.status})`);
      }

      setStatus('success');
    } catch (err) {
      setStatus('error');
      const raw = String(err?.message || '').toLowerCase();
      if (raw.includes('failed to fetch') || raw.includes('network')) {
        setErrorMsg('Unable to reach the careers service right now. Please try again in a moment.');
      } else {
        setErrorMsg(err.message || 'Something went wrong. Please try again.');
      }
    }
  }

  function handleOverlayClick(e) {
    if (e.target === overlayRef.current) onClose();
  }

  return (
    <div className="apply-modal-overlay" ref={overlayRef} onClick={handleOverlayClick} role="dialog" aria-modal="true" aria-labelledby="apply-modal-title">
      <div className="apply-modal">
        <button className="apply-modal-close" onClick={onClose} aria-label="Close">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>

        {status === 'success' ? (
          <div className="apply-modal-success">
            <div className="apply-modal-success-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#12683e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="9 12 11 14 15 10"/>
              </svg>
            </div>
            <h3>Application Submitted!</h3>
            <p>Thank you, <strong>{form.name}</strong>. Your application for <strong>{job.title}</strong> has been received. Our HR team will be in touch soon.</p>
            <button className="apply-modal-btn" onClick={onClose}>Close</button>
          </div>
        ) : (
          <>
            <h2 id="apply-modal-title" className="apply-modal-title">
              {job.title}
              <span className="apply-modal-id">ID: {job.id}</span>
            </h2>
            <p className="apply-modal-meta">{job.company} &bull; {job.location}</p>

            <form onSubmit={handleSubmit} noValidate className="apply-modal-form">
              <div className="apply-field">
                <label htmlFor="am-name">Full Name <span aria-hidden="true">*</span></label>
                <input
                  id="am-name"
                  ref={firstInputRef}
                  name="name"
                  type="text"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Jane Smith"
                  autoComplete="name"
                  aria-invalid={!!errors.name}
                />
                {errors.name && <p className="apply-field-error">{errors.name}</p>}
              </div>

              <div className="apply-field">
                <label htmlFor="am-email">Email <span aria-hidden="true">*</span></label>
                <input
                  id="am-email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="jane@example.com"
                  autoComplete="email"
                  aria-invalid={!!errors.email}
                />
                {errors.email && <p className="apply-field-error">{errors.email}</p>}
              </div>

              <div className="apply-field">
                <label htmlFor="am-phone">Phone <span aria-hidden="true">*</span></label>
                <input
                  id="am-phone"
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder={phonePlaceholder}
                  autoComplete="tel"
                  aria-invalid={!!errors.phone}
                />
                {errors.phone && <p className="apply-field-error">{errors.phone}</p>}
              </div>

              <div className="apply-field">
                <label htmlFor="am-resume">Resume <span aria-hidden="true">*</span> <span className="apply-field-hint">(PDF or Word, max 5 MB)</span></label>
                <input
                  id="am-resume"
                  name="resume"
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileChange}
                  aria-invalid={!!errors.resume}
                />
                {errors.resume && <p className="apply-field-error">{errors.resume}</p>}
              </div>

              {status === 'error' && (
                <p className="apply-submit-error">{errorMsg}</p>
              )}

              <button
                type="submit"
                className="apply-modal-btn apply-modal-btn--submit"
                disabled={status === 'submitting'}
              >
                {status === 'submitting' ? 'Submitting…' : 'Submit Application'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
